import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { saas } from "@/lib/api";
import { PageHeader, kr } from "@/components/Layout";
import { FormDialog } from "@/components/FormDialog";

const STATUS = [{ value: "utkast", label: "Utkast" }, { value: "sendt", label: "Sendt" }, { value: "betalt", label: "Betalt" }, { value: "forfalt", label: "Forfalt" }];
const BADGE = { utkast: "bg-secondary", sendt: "bg-blue-100 text-blue-800", betalt: "bg-green-100 text-green-800", forfalt: "bg-red-100 text-red-800" };
const EMPTY = { customer_id: "", invoice_number: "", amount: 0, due_date: "", status: "utkast" };

export default function Invoices() {
  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => saas.invoices.list().then(setList);
  useEffect(() => { load(); saas.customers.list().then(setCustomers); }, []);

  const fields = [
    { k: "customer_id", label: "Kunde", type: "select", full: true, options: [{ value: "", label: "– Velg kunde –" }, ...customers.map((c) => ({ value: c.id, label: c.name }))] },
    { k: "invoice_number", label: "Fakturanr" }, { k: "amount", label: "Beløp eks. mva", type: "number" },
    { k: "due_date", label: "Forfallsdato", type: "date" }, { k: "status", label: "Status", type: "select", options: STATUS },
  ];
  const body = (e) => ({ ...e, customer_id: e.customer_id || null, due_date: e.due_date || null, amount: Number(e.amount) || 0,
    customer_name: customers.find((c) => c.id === e.customer_id)?.name || "" });

  const save = async () => {
    edit.id ? await saas.invoices.update(edit.id, body(edit)) : await saas.invoices.create(body(edit));
    toast.success("Faktura lagret");
    setEdit(null);
    load();
  };
  const fromJobs = async () => {
    if (!edit.customer_id) return toast.error("Velg kunde først");
    const jobs = await saas.jobs.list({ customer_id: edit.customer_id, status: "fullfort" });
    if (!jobs.length) return toast.error("Ingen fullførte oppdrag for denne kunden");
    const r = await saas.invoiceFromJobs({ ...body(edit), job_ids: jobs.map((j) => j.id) });
    toast.success(`Faktura laget fra ${jobs.length} oppdrag: ${kr(r.amount)}`);
    setEdit(null);
    load();
  };
  const remove = async (i) => { if (window.confirm("Slette fakturaen?")) { await saas.invoices.remove(i.id); load(); } };
  const unpaid = list.filter((i) => i.status !== "betalt").reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="p-4 sm:p-8" data-testid="invoices-page">
      <PageHeader title="Fakturaer" subtitle={`Utestående inkl. mva: ${kr(unpaid)}`}>
        <Button data-testid="add-invoice-button" onClick={() => setEdit({ ...EMPTY })} className="gap-1.5"><Plus className="h-4 w-4" /> Ny faktura</Button>
      </PageHeader>
      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-foreground border-b border-border">
            <tr><th className="text-left p-3">Nr</th><th className="text-left p-3">Kunde</th><th className="text-right p-3">Eks. mva</th><th className="text-right p-3">Inkl. mva</th><th className="text-left p-3">Forfall</th><th className="text-left p-3">Status</th><th /></tr>
          </thead>
          <tbody>
            {list.map((i) => (
              <tr key={i.id} data-testid={`invoice-row-${i.id}`} className="border-b border-border last:border-0">
                <td className="p-3 font-mono text-xs">{i.invoice_number || "–"}</td>
                <td className="p-3 font-semibold">{i.customer_name || "–"}</td>
                <td className="p-3 text-right">{kr(i.amount)}</td>
                <td className="p-3 text-right font-bold">{kr(i.total)}</td>
                <td className="p-3">{i.due_date || "–"}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${BADGE[i.status]}`}>{STATUS.find((s) => s.value === i.status)?.label}</span></td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEdit({ ...EMPTY, ...i, customer_id: i.customer_id || "", due_date: i.due_date || "" })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">Ingen fakturaer enda.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">Fakturagrunnlag – send fakturaen via ditt regnskapsprogram (f.eks. Fiken/Tripletex). EHF/automatisk sending er ikke aktivert.</p>
      {edit && (
        <FormDialog open onOpenChange={() => setEdit(null)} title={edit.id ? "Rediger faktura" : "Ny faktura"} testid="invoice-dialog"
          fields={fields} value={edit} onChange={setEdit} onSubmit={save}>
          {!edit.id && <Button variant="outline" size="sm" className="gap-1.5 w-fit" data-testid="invoice-from-jobs-button" onClick={fromJobs}><Wand2 className="h-4 w-4" /> Lag fra kundens fullførte oppdrag</Button>}
        </FormDialog>
      )}
    </div>
  );
}
